/*
  Babblee · custom select
  ===========================================================================
  Enhances a real <select> rather than replacing it. The native element stays in
  the DOM, keeps its name and value, and is what the form submits — so with
  JavaScript off, or if this file fails to load, the control is still a working
  select rather than a button that does nothing.

  Applied to any element carrying [data-bb-select] wrapping a <select>.
*/
(() => {
  const OPEN = 'is-open';

  const enhance = (host) => {
    const select = host.querySelector('select');
    if (!select || host.dataset.bbSelectReady) return;
    host.dataset.bbSelectReady = '1';

    const id = select.id || `bb-select-${Math.round(performance.now() * 1000)}`;
    const listId = `${id}-list`;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bb-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', listId);
    // The native select keeps the accessible label; this mirrors it.
    const label = select.labels?.[0]?.textContent?.trim();
    if (label) button.setAttribute('aria-label', label);

    const value = document.createElement('span');
    value.className = 'bb-select__value';
    const caret = document.createElement('span');
    caret.className = 'bb-select__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<svg viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    button.append(value, caret);

    const list = document.createElement('div');
    list.className = 'bb-select__list';
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    const options = [...select.options];
    options.forEach((opt, i) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'bb-select__option';
      item.setAttribute('role', 'option');
      item.dataset.index = String(i);
      item.textContent = opt.textContent;
      item.setAttribute('aria-selected', String(opt.selected));
      list.append(item);
    });

    host.append(button, list);
    host.classList.add('bb-select');

    const paint = () => {
      value.textContent = select.options[select.selectedIndex]?.textContent || '';
      [...list.children].forEach((el, i) => el.setAttribute('aria-selected', String(i === select.selectedIndex)));
    };
    paint();

    let activeIndex = select.selectedIndex;

    const setActive = (i) => {
      activeIndex = Math.max(0, Math.min(options.length - 1, i));
      [...list.children].forEach((el, n) => el.classList.toggle('is-active', n === activeIndex));
      list.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
    };

    const open = () => {
      if (!list.hidden) return;
      list.hidden = false;
      host.classList.add(OPEN);
      button.setAttribute('aria-expanded', 'true');
      setActive(select.selectedIndex);
    };

    const close = () => {
      if (list.hidden) return;
      list.hidden = true;
      host.classList.remove(OPEN);
      button.setAttribute('aria-expanded', 'false');
    };

    const choose = (i) => {
      select.selectedIndex = i;
      paint();
      close();
      button.focus();
      /* A real change event, so anything already listening to the select — the
         collection form's onchange submit, for one — behaves exactly as it did
         before this file existed. */
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    button.addEventListener('click', () => (list.hidden ? open() : close()));

    list.addEventListener('click', (e) => {
      const item = e.target instanceof Element ? e.target.closest('[data-index]') : null;
      if (item) choose(Number(item.dataset.index));
    });

    host.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !list.hidden) { e.preventDefault(); close(); button.focus(); return; }
      if (list.hidden) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); open(); }
        return;
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
      if (e.key === 'Home') { e.preventDefault(); setActive(0); }
      if (e.key === 'End') { e.preventDefault(); setActive(options.length - 1); }
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(activeIndex); }
      if (e.key === 'Tab') close();
    });

    document.addEventListener('click', (e) => {
      if (e.target instanceof Node && !host.contains(e.target)) close();
    });

    // Keep in step if anything else changes the select.
    select.addEventListener('change', paint);
  };

  document.querySelectorAll('[data-bb-select]').forEach(enhance);
})();
